import {
  CircularProgress,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import {
  StyledHeaderTableCell,
  StyledIdTableCell,
  StyledTable,
} from '~/shared';

import type { UseGetCampaignMetricsReturn } from '../../hooks/useGetCampaignMetrics';

interface CampaignMetricsTableProps {
  data: UseGetCampaignMetricsReturn;
  isLoading: boolean;
  isError: boolean;
  metricType?: 'CTR' | 'CPI' | 'CPC';
}

export function CampaignMetricsTable({
  data,
  isLoading,
  isError,
  metricType = 'CTR',
}: CampaignMetricsTableProps) {
  const campaigns = data?.list || [];

  const formatNumber = (value: number | string | undefined): string => {
    if (value === undefined || value === null) return '0';
    const numValue = typeof value === 'number' ? value : Number(value);
    return numValue.toLocaleString('pt-BR');
  };

  return (
    <StyledTable aria-label="Tabela de Métricas de Campanhas">
      <TableHead>
        <TableRow>
          <StyledHeaderTableCell sx={{ width: '80px' }} align="center">
            ID
          </StyledHeaderTableCell>
          <StyledHeaderTableCell>Campanha</StyledHeaderTableCell>
          <StyledHeaderTableCell>Anunciante</StyledHeaderTableCell>
          <StyledHeaderTableCell>Tipo</StyledHeaderTableCell>
          <StyledHeaderTableCell>Status</StyledHeaderTableCell>
          <StyledHeaderTableCell>Período</StyledHeaderTableCell>
          <StyledHeaderTableCell>Segmentação</StyledHeaderTableCell>
          <StyledHeaderTableCell align="right">
            Impressões
          </StyledHeaderTableCell>
          <StyledHeaderTableCell align="right">Cliques</StyledHeaderTableCell>
          <StyledHeaderTableCell align="right">
            {metricType}
          </StyledHeaderTableCell>
        </TableRow>
      </TableHead>

      <TableBody>
        {isLoading && (
          <TableRow>
            <TableCell component="th" scope="row" colSpan={10} align="center">
              <CircularProgress size={15} /> Carregando
            </TableCell>
          </TableRow>
        )}

        {!isLoading && !isError && campaigns.length === 0 && (
          <TableRow>
            <TableCell colSpan={10} align="center">
              Sem resultados
            </TableCell>
          </TableRow>
        )}

        {isError && (
          <TableRow>
            <TableCell
              sx={theme => ({ color: theme.palette.error.light })}
              colSpan={10}
              align="center"
            >
              <Typography>
                Erro ao carregar métricas de campanhas. As datas de início e
                término são obrigatórias.
              </Typography>
            </TableCell>
          </TableRow>
        )}

        {!isLoading &&
          !isError &&
          campaigns.map(campaign => (
            <TableRow
              key={campaign.campaignId}
              sx={{
                whiteSpace: 'nowrap',
                '&:hover': {
                  '.MuiTableCell-body': {
                    backgroundColor: theme => theme.palette.grey[100],
                  },
                },
                '.MuiTableCell-body': {
                  backgroundColor: 'background.paper',
                },
              }}
            >
              <StyledIdTableCell align="center">
                {campaign.campaignId}
              </StyledIdTableCell>
              <TableCell component="th" scope="row">
                {campaign.campaignName || '-'}
              </TableCell>
              <TableCell component="th" scope="row">
                {campaign.advertiserName || '-'}
              </TableCell>
              <TableCell component="th" scope="row">
                {campaign.tipoCampanha || '-'}
              </TableCell>
              <TableCell component="th" scope="row">
                {campaign.status || '-'}
              </TableCell>
              <TableCell component="th" scope="row">
                {campaign.periodo || '-'}
              </TableCell>
              <TableCell component="th" scope="row">
                {campaign.segmentacao || '-'}
              </TableCell>
              <TableCell component="th" scope="row" align="right">
                {formatNumber(campaign.impressions)}
              </TableCell>
              <TableCell component="th" scope="row" align="right">
                {formatNumber(campaign.clicks)}
              </TableCell>
              <TableCell component="th" scope="row" align="right">
                {campaign.ctr || '-'}
              </TableCell>
            </TableRow>
          ))}
      </TableBody>
    </StyledTable>
  );
}
